import React, { useState } from 'react';
import useLocalStorage from '../hooks/useLocalStorage';
import NoteForm from '../components/NoteForm';
import NoteCard from '../components/NoteCard';
import Filters from '../components/Filters';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Grid,
  Box,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';

const STORAGE_KEY = 'personal_notes_app_v1';

export default function Home() {
  const [notes, setNotes] = useLocalStorage(STORAGE_KEY, []);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  function addNote(e) {
    e.preventDefault();
    if (!title.trim() && !body.trim()) return;
    const newNote = {
      id: Date.now().toString(),
      title: title.trim() || 'Untitled',
      body: body.trim(),
      completed: false,
      createdAt: new Date().toISOString(),
    };
    setNotes((s) => [newNote, ...s]);
    setTitle('');
    setBody('');
  }

  function startEdit(note) {
    setEditingId(note.id);
    setTitle(note.title);
    setBody(note.body);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function saveEdit(e) {
    e.preventDefault();
    if (!editingId) return;
    setNotes((s) => s.map((n) => (n.id === editingId ? { ...n, title, body } : n)));
    cancelEdit();
  }

  function cancelEdit() {
    setEditingId(null);
    setTitle('');
    setBody('');
  }

  function deleteNote(id) {
    if (!window.confirm('Delete this note?')) return;
    setNotes((s) => s.filter((n) => n.id !== id));
  }

  function toggleCompleted(id) {
    setNotes((s) => s.map((n) => (n.id === id ? { ...n, completed: !n.completed } : n)));
  }

  function filteredNotes() {
    const q = query.trim().toLowerCase();
    return notes.filter((n) => {
      if (filter === 'completed' && !n.completed) return false;
      if (filter === 'pending' && n.completed) return false;
      if (!q) return true;
      return (n.title + ' ' + n.body).toLowerCase().includes(q);
    });
  }

  const total = notes.length;
  const completedCount = notes.filter((n) => n.completed).length;

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <StickyNote2Icon sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Personal Notes App
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
        <NoteForm
          title={title}
          body={body}
          setTitle={setTitle}
          setBody={setBody}
          onSubmit={editingId ? saveEdit : addNote}
          onCancel={cancelEdit}
          isEditing={Boolean(editingId)}
        />

        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, mb: 2 }}>
          <TextField
            placeholder="Search notes..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Filters filter={filter} setFilter={setFilter} />
            <Typography variant="body2" color="text.secondary">
              <strong>{total}</strong> total • <strong>{completedCount}</strong> completed
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2}>
          {filteredNotes().length === 0 ? (
            <Grid item xs={12}>
              <Box sx={{ p: 6, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 1 }}>
                No notes match.
              </Box>
            </Grid>
          ) : (
            filteredNotes().map((note) => (
              <Grid item xs={12} sm={6} md={4} key={note.id}>
                <NoteCard note={note} onEdit={startEdit} onDelete={deleteNote} onToggle={toggleCompleted} />
              </Grid>
            ))
          )}
        </Grid>
      </Container>
    </>
  );
}
